import app from 'flarum/forum/app';
import Page from 'flarum/common/components/Page';
import Icon from 'flarum/common/components/Icon';
import GeewenNav from './GeewenNav';
import GeewenFooter from './GeewenFooter';
import { t, tList, tItems } from '../helpers/i18n';
import { gs, gsBool, productPrefix } from '../helpers/settings';
import { findProduct } from '../products';

/**
 * GeewenProductPage.jsx —— /geewen/:slug 产品详情页
 *
 * 通用骨架 + locale 文案 ⇒ 以后加第 3、第 4 个产品只要:
 *   ① js/src/forum/products.js 里加一条注册项 (slug / icon / accent)
 *   ② locale 的 forum.products.<slug> 里补一段文案
 *
 * ⚠️ mithril 0.2.x 的两条硬规则 (踩过两次):
 *   1) ⛔ Flarum 2.0 的 `ItemList` **不是组件** ⇒ 列表一律用 map 出来的普通数组。
 *   2) ⛔ 同一个 fragment (数组) 里的 vnode 必须「全有 key」或「全没 key」, 不能混;
 *      且 children 里不能留 null hole ⇒ 条件渲染用数组 push。
 */
export default class GeewenProductPage extends Page {
  // 走 vendor Page 的 bodyClass 机制 ($('#app').addClass), 供 less 里做论坛外壳的微调
  bodyClass = 'App--geewen';
  scrollTopOnCreate = false;
  oncreate(vnode) {
    super.oncreate(vnode);
    this.setTitle();
  }

  /** 当前路由里的产品 slug */
  slug() {
    try {
      const slug = m.route.param('slug');
      if (slug) return String(slug).replace(/\/+$/, '');
    } catch (e) {
      // m.route.param 不可用时退回到解析路径
    }
    const match = (m.route.get() || '').match(/\/geewen\/([^/?#]+)/);
    return match ? match[1] : '';
  }
  setTitle() {
    const product = findProduct(this.slug());
    if (!product) {
      app.setTitle(t('product_page.notfound_title'));
      app.setTitleCount(0);
      return;
    }
    const name = this.productName(product);
    app.setTitle(name);
    app.setTitleCount(0);
    document.title = name + ' · ' + gs('geewen_brand_name', t('meta.brand_name'));
  }

  /** 产品名 (后台可配, 留空回落到 locale) */
  productName(product) {
    return gs(productPrefix(product.slug) + '_name', t('products.' + product.slug + '.name'));
  }

  /** 产品副名 (后台可配, 留空回落到 locale) */
  productSub(product) {
    return gs(productPrefix(product.slug) + '_sub', t('products.' + product.slug + '.sub'));
  }

  /** 产品一句话定位 (后台可配, 留空回落到 locale) */
  productTagline(product) {
    return gs(productPrefix(product.slug) + '_tagline', t('products.' + product.slug + '.tagline'));
  }

  /** 产品是否在官网展示 (后台可关) */
  productVisible(product) {
    return gsBool(productPrefix(product.slug) + '_visible', true);
  }

  /** 没找到产品时的占位内容 */
  notFound() {
    return m("section", {
      class: "GS-section GS-section--flush GS-pnotfound"
    }, m("div", {
      class: "GS-container"
    }, m("div", {
      class: "GS-pnotfound-box"
    }, m("span", {
      class: "GS-pnotfound-icon"
    }, m(Icon, {
      name: "fas fa-compass"
    })), m("h1", {
      class: "GS-pnotfound-title"
    }, t('product_page.notfound_title')), m("p", {
      class: "GS-pnotfound-desc"
    }, t('product_page.notfound_desc')), m("a", {
      class: "GS-btn GS-btn--primary GS-btn--lg",
      href: "/geewen"
    }, m(Icon, {
      name: "fas fa-arrow-left"
    }), t('product_page.back')))));
  }

  /** 产品头部: 名称 / 副名 / 一句话定位 / 详细介绍 / 关键数字 */
  hero(product) {
    const slug = product.slug;
    const stats = tItems('products.' + slug + '.stats', ['num', 'label']).map(stat => m("li", {
      class: "GS-pstat",
      key: stat.label
    }, m("span", {
      class: "GS-pstat-num"
    }, stat.num), m("span", {
      class: "GS-pstat-label"
    }, stat.label)));
    return m("section", {
      class: "GS-phero GS-phero--" + product.accent
    }, m("div", {
      class: "GS-phero-glow"
    }), m("div", {
      class: "GS-container GS-phero-inner"
    }, m("div", {
      class: "GS-phero-copy"
    }, m("span", {
      class: "GS-phero-icon"
    }, m(Icon, {
      name: product.icon
    })), m("h1", {
      class: "GS-phero-title"
    }, this.productName(product)), m("p", {
      class: "GS-phero-sub"
    }, this.productSub(product)), m("p", {
      class: "GS-phero-tagline"
    }, this.productTagline(product)), m("p", {
      class: "GS-phero-intro"
    }, t('products.' + slug + '.intro')), m("div", {
      class: "GS-phero-cta"
    }, m("a", {
      class: "GS-btn GS-btn--primary GS-btn--lg",
      href: "/"
    }, t('product_page.cta_primary'), m(Icon, {
      name: "fas fa-arrow-right"
    })), m("a", {
      class: "GS-btn GS-btn--ghost GS-btn--lg",
      href: "/geewen#gs-products"
    }, t('product_page.cta_secondary')))), m("aside", {
      class: "GS-phero-panel"
    }, m("ul", null, stats))));
  }

  /** 功能模块 (12 个 / 4 步) */
  modules(product) {
    const slug = product.slug;
    const items = tList('products.' + slug + '.modules').map(name => m("li", {
      class: "GS-module",
      key: name
    }, m(Icon, {
      name: "fas fa-check"
    }), m("span", null, name)));
    return m("section", {
      class: "GS-section GS-section--alt",
      id: "gs-modules"
    }, m("div", {
      class: "GS-container"
    }, m("div", {
      class: "GS-section-head"
    }, m("h2", {
      class: "GS-section-title"
    }, t('products.' + slug + '.modules_title')), m("p", {
      class: "GS-section-desc"
    }, t('products.' + slug + '.modules_desc'))), m("ul", {
      class: "GS-modules"
    }, items)));
  }

  /** 核心能力 (6 张带图标的卡) */
  features(product) {
    const slug = product.slug;
    const cards = tItems('products.' + slug + '.features', ['icon', 'title', 'desc']).map(feature => m("div", {
      class: "GS-tile GS-feature-tile",
      key: feature.title
    }, m("span", {
      class: "GS-tile-icon"
    }, m(Icon, {
      name: feature.icon
    })), m("h3", {
      class: "GS-tile-title"
    }, feature.title), m("p", {
      class: "GS-tile-desc"
    }, feature.desc)));
    return m("section", {
      class: "GS-section",
      id: "gs-features"
    }, m("div", {
      class: "GS-container"
    }, m("div", {
      class: "GS-section-head"
    }, m("h2", {
      class: "GS-section-title"
    }, t('products.' + slug + '.features_title'))), m("div", {
      class: "GS-grid GS-grid--3"
    }, cards)));
  }

  /** 常见问题 (原生 details 折叠, 免 JS) */
  faq(product) {
    const slug = product.slug;
    const items = tItems('products.' + slug + '.faq', ['q', 'a']).map((item, index) => m("li", {
      key: item.q
    }, m("details", {
      class: "GS-faq-item",
      open: index === 0
    }, m("summary", {
      class: "GS-faq-q"
    }, m("span", null, item.q), m(Icon, {
      name: "fas fa-chevron-down"
    })), m("div", {
      class: "GS-faq-a"
    }, m("p", null, item.a)))));
    return m("section", {
      class: "GS-section GS-section--alt",
      id: "gs-faq"
    }, m("div", {
      class: "GS-container GS-faq-wrap"
    }, m("div", {
      class: "GS-section-head"
    }, m("h2", {
      class: "GS-section-title"
    }, t('product_page.faq_title'))), m("ul", {
      class: "GS-faq"
    }, items)));
  }

  /** 结尾 CTA */
  cta() {
    return m("section", {
      class: "GS-section GS-section--flush",
      id: "gs-cta"
    }, m("div", {
      class: "GS-container"
    }, m("div", {
      class: "GS-cta"
    }, m("div", {
      class: "GS-cta-glow"
    }), m("h2", {
      class: "GS-cta-title"
    }, t('product_page.cta_title')), m("p", {
      class: "GS-cta-desc"
    }, t('product_page.cta_desc')), m("div", {
      class: "GS-cta-actions"
    }, m("a", {
      class: "GS-btn GS-btn--primary GS-btn--lg",
      href: "/"
    }, t('product_page.cta_primary'), m(Icon, {
      name: "fas fa-arrow-right"
    })), m("a", {
      class: "GS-btn GS-btn--ghost GS-btn--lg",
      href: "/geewen#gs-products"
    }, t('product_page.cta_secondary'))))));
  }
  view() {
    const product = findProduct(this.slug());
    const rootClass = 'GS-root GS-root--product' + (product ? ' GS-root--' + product.accent : '');

    // 后台把产品「隐藏」了 ⇒ 跟「没这个产品」一样处理 (文案: 也许还在开发中)
    const hidden = product ? !this.productVisible(product) : false;
    if (!product || hidden) {
      return m("div", {
        class: rootClass
      }, m(GeewenNav, {
        anchorBase: "/geewen"
      }), m("main", {
        class: "GS-main"
      }, this.notFound()), m(GeewenFooter, {
        anchorBase: "/geewen"
      }));
    }
    return m("div", {
      class: rootClass
    }, m(GeewenNav, {
      anchorBase: "/geewen"
    }), m("main", {
      class: "GS-main"
    }, m("div", {
      class: "GS-container GS-pback"
    }, m("a", {
      class: "GS-pback-link",
      href: "/geewen"
    }, m(Icon, {
      name: "fas fa-arrow-left"
    }), t('product_page.back'))), this.hero(product), this.modules(product), this.features(product), this.faq(product), this.cta()), m(GeewenFooter, {
      anchorBase: "/geewen"
    }));
  }
}
flarum.reg.add('ziiven-geewen-site', 'forum/components/GeewenProductPage', GeewenProductPage);